/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
//mmx-feature: imports --start
/*
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxState,
    IQuickviewViewProps
} from '@msdyn365-commerce-modules/buybox';
*/

import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxState
} from '../../../modules/mmx-buybox/common';
import { IMmxQuickviewData, IQuickviewViewProps } from '../../../modules/mmx-quickview';
import ProductWarrantyComponent from '../../../components/product-warranty/product-warranty';
import { IWarrantyOption } from '../../../actions/DataServiceEntities.g';
import { getSelectedProductIdFromActionInput } from '../../../utilities/utils';
import { getEmptyWarrantyOption } from '../../../utilities/warranty/utils';
import { IProductWarrantyComponentProps } from '../../../components/product-warranty/product-warranty.props';
import { ProductWarrantyStateInput } from '../../../actions/state-data-actions/product-warranty-state.action';
//mmx-feature: imports --end
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

//mmx-feature: imports --start
/*
import { IQuickviewProps as IQuickviewPropsExtend } from '../definition-extensions/quickview.ext.props.autogenerated';
*/
import { IMmxQuickviewProps as IQuickviewExtentedProps } from '../definition-extensions/mmx-quickview.ext.props.autogenerated';
//mmx-features: imports --end

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
//mmx-feature: add to cart --begin
/*
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
*/
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps, storeSelector: React.ReactNode): JSX.Element => {
//mmx-feature: add to cart --end
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {/*mmx-feature: add to cart --start*/
            storeSelector
            /*mmx-features: add to cart --end*/}
            {button}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (
    callbacks: IBuyboxCallbacks | undefined,
    resources: IBuyboxCommonResources,
    product: SimpleProduct | undefined
) => (result: IWishlistActionErrorResult) => {
    callbacks?.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IQuickviewViewProps,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks | undefined,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IQuickviewViewProps,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks | undefined
): JSX.Element | null => {
    const product = props.productDetails?.product;
    if (!props.addToWishlist || !product) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RenderQuantity.
 * @param quantity - Quantity.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param props - Props.
 * @returns -- Returns.
 */
//mmx-feature: quantity disable --start
/*
const renderQuantity = (
    quantity: IBuyboxProductQuantityViewProps,
    quantityLimitsMessages: React.ReactNode,
    props: IQuickviewViewProps
): JSX.Element => {
*/
const renderQuantity = (
    quantity: IBuyboxProductQuantityViewProps, 
    quantityLimitsMessages: React.ReactNode, 
    props: IQuickviewViewProps & IQuickviewExtentedProps<IMmxQuickviewData>,
): JSX.Element => {
    //mmx-feature: quantity disable --end
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;
    const { unitOfMeasure } = props;

    //mmx-feature: quantity disable --start
    const selectedWarranty = props.state.selectedWarrantyOption;
    if (props?.context.app.config.disableWarrantyOnMultipleQuantityProducts && selectedWarranty && selectedWarranty.Period > 0) {
        //@ts-ignore-error
        const quantity = input?.props?.currentCount;
        return <Node className='quantity'>Quantity: {quantity}</Node>;
    }
    //mmx-feature: quantity disable --end

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
            {unitOfMeasure}
            {quantityLimitsMessages}
            {errors}
        </Node>
    );
};

/**
 * KenderKeyInPrice.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

//mmx-feature: default variant --start
/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
/*
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};
*/

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
//mmx-feature: default configuration --start
/*
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};
*/
//mmx-feature: default configuration --end

//mmx-feature: product warranty --start
const renderWarrantySelectorComponentByQuantity = (
    productWarrantyComponentProps: IProductWarrantyComponentProps,
    props: IQuickviewViewProps & IQuickviewExtentedProps<IMmxQuickviewData>,
    state: any,
    isMultipleQuantity: boolean,
    setIsMultipleQuantity: any
): JSX.Element | undefined => {
    if (props?.context.app.config.disableWarrantyOnMultipleQuantityProducts && state.quantity > 1) {
        if (!isMultipleQuantity) {
            let productId = getSelectedProductIdFromActionInput(props.context.actionContext);
            if (productId && productWarrantyComponentProps?.productWarranties?.WarrantyGroups) {
                const warrantyOptions = productWarrantyComponentProps?.productWarranties?.WarrantyGroups
                ? productWarrantyComponentProps.productWarranties.WarrantyGroups[0].WarrantyOptions
                : undefined;
                let emptyWarranty = getEmptyWarrantyOption(+productId, warrantyOptions);
                props.context.actionContext.update<IWarrantyOption | undefined>(new ProductWarrantyStateInput(+productId), emptyWarranty);
            }
            setIsMultipleQuantity(true);
        }
        productWarrantyComponentProps.isSelectWarrantyDisabled = true;
        return <ProductWarrantyComponent {...productWarrantyComponentProps} {...{ productQuantity: state.quantity }} />;
    } else {
        if (props?.context.app.config.disableWarrantyOnMultipleQuantityProducts && isMultipleQuantity) {
            setIsMultipleQuantity(false);
        }
        return renderWarrantySelectorComponent(productWarrantyComponentProps);
    }
};

const renderWarrantySelectorComponent = (productWarrantyComponentProps: IProductWarrantyComponentProps) => {
    return <ProductWarrantyComponent {...productWarrantyComponentProps} />;
};
//mmx-feature: product warranty --end

/**
 * RenderBodyContent.
 * @param props - Props.
 * @returns -- Returns.
 */
//mmx-feature: product warranty/quantity --start
/*
const renderBodyContent = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
    const {
        price,
        keyInPrice,
        quantity,
        inventoryLabel,
        quantityLimitsMessages,
        loading,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
        ProductInfoContainerProps,
        MediaGalleryContainerProps,
        CarouselProps,
        addToWishlist,
        configure,
        callbacks,
        state
    } = props;
    */
const renderBodyContent = (props: IQuickviewViewProps & IQuickviewExtentedProps<IMmxQuickviewData>): JSX.Element => {
    const {
        price,
        keyInPrice,
        quantity,
        inventoryLabel,
        quantityLimitsMessages,
        loading,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
        ProductInfoContainerProps,
        MediaGalleryContainerProps,
        CarouselProps,
        productWarrantyComponentProps,
        addToWishlist,
        callbacks,
        state
    } = props;
    const [isMultipleQuantity, setIsMultipleQuantity] = React.useState<boolean>(false);
    //mmx-feature: selected warranty disable function --end
    if (loading) {
        return <>{loading}</>;
    }

    return (
        <>
            <Node {...MediaGalleryContainerProps}>
                <Node {...CarouselProps} />
            </Node>
            <Node {...ProductInfoContainerProps}>
                <Node className='msc-quickview__price-section'>
                    <Node className='msc-quickview__price-section-text'>
                        {props.resources.priceText}
                        {price}
                    </Node>
                    {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                </Node>
                {//mmx-feature: pickup option --start
                /*
                {configure && renderConfigure(configure)}
                */
                props.context.app.config.productWarrantyEnabled && productWarrantyComponentProps
                    ? renderWarrantySelectorComponentByQuantity(
                          productWarrantyComponentProps,
                          props,
                          props.state,
                          isMultipleQuantity,
                          setIsMultipleQuantity
                      )
                    : <></>
                //mmx-feature: pickup option --end
                }
                {keyInPrice && renderKeyInPrice(keyInPrice)}
                {quantity && renderQuantity(quantity, quantityLimitsMessages, props)}
                {inventoryLabel}
            </Node>
        </>
    );
};

/**
 * RenderQuickViewPopup.
 * @param props - Props.
 * @returns -- Returns.
 */
//mmx-feature: product warranty --start
/*
const renderQuickViewPopup = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
*/
const renderQuickViewPopup = (props: IQuickviewViewProps & IQuickviewExtentedProps<IMmxQuickviewData>): JSX.Element => {
    //mmx-feature: product warranty --end
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const {
        ModalContainer,
        ModalHeaderContainer,
        ModalFooterContainer,
        ModalHeaderContent,
        ModalBodyContainer,
        addToCart,
        cartContainerProps,
        seeDetailsbutton
    } = props;

    return (
        <Module {...ModalContainer}>
            <Node {...ModalHeaderContainer}>
                {ModalHeaderContent}
                <Node className='msc-quickview__heading'>
                    {props.title}
                    {!props.loading && !StringExtensions.isNullOrEmpty(props.state.productPrice?.ItemId) ? (
                        <Node className='msc-quickview__sku-text'>
                            {props.resources.skuText}
                            {props.state.productPrice?.ItemId}
                        </Node>
                    ) : (
                        ''
                    )}
                    {props.rating}
                </Node>
            </Node>
            <Node {...ModalBodyContainer}>{renderBodyContent(props)}</Node>
            <Node {...ModalFooterContainer}>
                <Node {...cartContainerProps}>
                    {addToCart && seeDetailsbutton}
                    {/*mmx-feature: product warranty --start*/
                    /*
                    {addToCart && renderAddToCart(addToCart)}
                    */
                    addToCart && renderAddToCart(addToCart, props.slots.storeSelector)
                    /*mmx-feature: product warranty --end*/}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param props - Configuration for the quick view component.
 * @returns - Functional component of the quick view.
 */
//mmx-feature: extended properties --start
/*
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & IQuickviewPropsExtend<{}>> = (
    props: IQuickviewViewProps & IQuickviewPropsExtend<{}>
) => {
*/
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & IQuickviewExtentedProps<IMmxQuickviewData>> = (
    props: IQuickviewViewProps & IQuickviewExtentedProps<IMmxQuickviewData>) => {
    //mmx-feature: extended properties --end
    const { quickView, quickViewButton, isModalOpen } = props;
    const quickViewUpdatedProps: IModuleProps = { ...quickView, title: props.resources.quickViewbuttonText };
    return (
        <Module {...quickViewUpdatedProps}>
            {quickViewButton}
            {isModalOpen && renderQuickViewPopup(props)}
        </Module>
    );
};

export default QuickViewFunctionalComponent;
